import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import {
  MD_CASE_PROMOTION_STATES,
  MD_CASE_SEVERITIES,
  MD_CASE_SOURCE_TYPES,
  MD_CASE_STATUSES,
  MD_CASE_WORKFLOW_MODES,
  type MdCasePromotionState,
  type MdCaseSeverity,
  type MdCaseSourceType,
  type MdCaseStatus,
  type MdCaseWorkflowMode,
} from 'features/mdCases/mdCase'
import { z } from 'zod'

import {
  MD_CASE_PUBLISHED_STATES,
  type MdCasesListFilters,
  type MdCasesPublishedState,
} from './mdCasesListViewModel'

export const MD_CASES_LIST_FILTER_CONFIG = {
  status: {
    label: msg`Status`,
    options: MD_CASE_STATUSES,
    viewModelKey: 'status',
  },
  severity: {
    label: msg`Severity`,
    options: MD_CASE_SEVERITIES,
    viewModelKey: 'severity',
  },
  workflowMode: {
    label: msg`Workflow Mode`,
    options: MD_CASE_WORKFLOW_MODES,
    viewModelKey: 'workflow_mode',
  },
  sourceType: {
    label: msg`Source Type`,
    options: MD_CASE_SOURCE_TYPES,
    viewModelKey: 'source_type',
  },
  promotionState: {
    label: msg`Promotion State`,
    options: MD_CASE_PROMOTION_STATES,
    viewModelKey: 'promotion_state',
  },
  publishedState: {
    label: msg`Published State`,
    options: MD_CASE_PUBLISHED_STATES,
    viewModelKey: 'published_state',
  },
} as const satisfies Record<
  string,
  {
    label: MessageDescriptor
    options: readonly string[]
    viewModelKey: keyof MdCasesListFilters
  }
>

export type MdCasesListFilterSearchKey =
  keyof typeof MD_CASES_LIST_FILTER_CONFIG

export type MdCasesListFilterSearch = {
  status?: readonly MdCaseStatus[]
  severity?: readonly MdCaseSeverity[]
  workflowMode?: readonly MdCaseWorkflowMode[]
  sourceType?: readonly MdCaseSourceType[]
  promotionState?: readonly MdCasePromotionState[]
  publishedState?: readonly MdCasesPublishedState[]
}

export const MD_CASES_LIST_FILTER_KEYS = Object.keys(
  MD_CASES_LIST_FILTER_CONFIG
) as MdCasesListFilterSearchKey[]

export const MD_CASES_LIST_FILTER_SEARCH_PARAM_SHAPE = {
  status: z.array(z.enum(MD_CASE_STATUSES)).optional(),
  severity: z.array(z.enum(MD_CASE_SEVERITIES)).optional(),
  workflowMode: z.array(z.enum(MD_CASE_WORKFLOW_MODES)).optional(),
  sourceType: z.array(z.enum(MD_CASE_SOURCE_TYPES)).optional(),
  promotionState: z.array(z.enum(MD_CASE_PROMOTION_STATES)).optional(),
  publishedState: z.array(z.enum(MD_CASE_PUBLISHED_STATES)).optional(),
}

export const EMPTY_MD_CASES_LIST_FILTER_SEARCH: Readonly<
  Record<MdCasesListFilterSearchKey, undefined>
> = Object.fromEntries(
  MD_CASES_LIST_FILTER_KEYS.map((searchKey) => [searchKey, undefined])
) as Record<MdCasesListFilterSearchKey, undefined>

export const buildMdCasesListFilters = (
  search: MdCasesListFilterSearch
): MdCasesListFilters => ({
  status: search.status,
  severity: search.severity,
  workflow_mode: search.workflowMode,
  source_type: search.sourceType,
  promotion_state: search.promotionState,
  published_state: search.publishedState,
})
